import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { ShieldCheck, Copy, Download, ArrowRight, Sparkles } from "lucide-react";

/**
 * Two-factor auth enrollment page. Reached after login when the backend
 * returned `requires_2fa_setup: true` for an admin account.
 *
 * Flow:  QR scan → type 6-digit code → activate → show backup codes → done.
 */
export default function TwoFactorSetup() {
  const navigate = useNavigate();
  const [step, setStep] = useState("start"); // start | qr | verify | done
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState(null); // { secret, qr_png, backup_codes, ... }
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);

  const startSetup = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/setup", {});
      setSetupData(data);
      setStep("qr");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible de démarrer la configuration 2FA");
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    try {
      await api.post("/auth/2fa/verify", { code: code.trim() });
      toast.success("2FA activée");
      setStep("done");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Code incorrect");
    } finally {
      setLoading(false);
    }
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(setupData?.secret || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Copie impossible"); }
  };

  const downloadCodes = () => {
    const text = [
      "Prosperr — Codes de secours 2FA",
      "Conservez ce fichier dans un endroit sûr. Chaque code ne fonctionne qu'une seule fois.",
      `Compte : ${setupData?.account}`,
      `Émis le : ${new Date().toLocaleString("fr-FR")}`,
      "",
      ...(setupData?.backup_codes || []),
    ].join("\n");
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `prosperr-codes-secours-2fa.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] antialiased">
      <header className="sticky top-0 z-40 bg-[rgba(9,9,11,0.85)] backdrop-blur-md border-b border-[var(--border)]">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-black grid place-items-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-bold text-xl tracking-tight">Prosperr</span>
          </Link>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-400">
            <ShieldCheck className="w-4 h-4" /> Sécurité 2FA
          </div>
        </div>
      </header>

      <section className="max-w-2xl mx-auto px-6 py-16 sm:py-20" data-testid="2fa-setup-page">
        {step === "start" && (
          <>
            <div className="text-xs font-bold uppercase tracking-widest text-zinc-400">Sécurité</div>
            <h1 className="mt-3 font-display font-bold text-[var(--text)] tracking-tight leading-[0.95] text-4xl sm:text-5xl">
              Protégez votre compte.
            </h1>
            <p className="mt-6 text-base text-zinc-300 leading-relaxed max-w-lg">
              Ajoutez une couche de sécurité supplémentaire en activant la double authentification (2FA).
              Vous aurez besoin d'une application comme <strong>Google Authenticator</strong>, <strong>Authy</strong> ou <strong>1Password</strong>.
            </p>
            <div className="mt-10 flex gap-3">
              <Button
                onClick={startSetup}
                disabled={loading}
                className="rounded-full bg-black hover:bg-black/85 text-white h-14 px-8 text-base font-medium"
                data-testid="2fa-start"
              >
                {loading ? "Préparation…" : "Commencer la configuration"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </>
        )}

        {step === "qr" && setupData && (
          <>
            <div className="text-xs font-bold uppercase tracking-widest text-zinc-400">Étape 1 sur 2</div>
            <h1 className="mt-3 font-display font-bold text-[var(--text)] tracking-tight leading-[1] text-3xl sm:text-4xl">
              Scannez ce QR code.
            </h1>
            <p className="mt-4 text-sm text-zinc-300 leading-relaxed">
              Ouvrez votre application d'authentification et ajoutez un nouveau compte en scannant le QR ci-dessous.
            </p>
            <div className="mt-8 grid sm:grid-cols-[auto_1fr] gap-8 items-start">
              <div className="border border-[var(--border)] p-4 bg-white grid place-items-center w-[240px] h-[240px] rounded-lg shrink-0">
                <img src={setupData.qr_png} alt="QR Code 2FA" className="w-full h-full" />
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Saisie manuelle</div>
                <div className="flex items-center gap-2">
                  <code className="block flex-1 text-sm font-mono bg-[var(--surface-alt)] border border-[var(--border)] rounded-md px-3 py-2 tracking-widest break-all">
                    {setupData.secret}
                  </code>
                  <button
                    onClick={copySecret}
                    className="rounded-full border border-[var(--border)] px-3 h-10 text-xs font-semibold hover:bg-emerald-500 hover:text-white transition-colors inline-flex items-center gap-1.5"
                    data-testid="2fa-copy-secret"
                  >
                    <Copy className="w-3.5 h-3.5" /> {copied ? "Copié" : "Copier"}
                  </button>
                </div>
                <p className="mt-3 text-xs text-zinc-500 leading-relaxed">
                  Compte : <strong>{setupData.account}</strong> · Émetteur : Prosperr
                </p>
              </div>
            </div>

            {/* Backup codes */}
            <div className="mt-10 border border-[var(--border)] rounded-2xl p-6 bg-[var(--surface-alt)]">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="font-display font-bold text-lg">Vos 10 codes de secours</div>
                  <div className="text-xs text-zinc-400">Utilisables une seule fois en cas de perte de téléphone.</div>
                </div>
                <button
                  onClick={downloadCodes}
                  className="rounded-full bg-black text-white h-10 px-4 text-xs font-semibold hover:bg-black/85 inline-flex items-center gap-1.5"
                  data-testid="2fa-download-codes"
                >
                  <Download className="w-3.5 h-3.5" /> Télécharger (.txt)
                </button>
              </div>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-2">
                {setupData.backup_codes.map((c) => (
                  <code key={c} className="text-center text-sm font-mono bg-[var(--surface-alt)] border border-[var(--border)] text-[var(--text)] rounded-md py-2 tracking-wider">{c}</code>
                ))}
              </div>
              <p className="mt-4 text-[11px] text-red-700">
                ⚠ Ces codes ne seront plus jamais réaffichés. Sauvegardez-les maintenant.
              </p>
            </div>

            <div className="mt-10">
              <Button
                onClick={() => setStep("verify")}
                className="rounded-full bg-black hover:bg-black/85 text-white h-14 px-8 text-base font-medium"
                data-testid="2fa-next"
              >
                J'ai scanné — vérifier
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </>
        )}

        {step === "verify" && (
          <>
            <div className="text-xs font-bold uppercase tracking-widest text-zinc-400">Étape 2 sur 2</div>
            <h1 className="mt-3 font-display font-bold text-[var(--text)] tracking-tight leading-[1] text-3xl sm:text-4xl">
              Entrez le code à 6 chiffres.
            </h1>
            <p className="mt-4 text-sm text-zinc-300">
              Pour activer la 2FA, tapez le code actuellement affiché dans votre application.
            </p>
            <form onSubmit={verify} className="mt-8 max-w-sm">
              <Input
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\s/g, ""))}
                inputMode="numeric"
                maxLength={6}
                placeholder="123 456"
                data-testid="2fa-verify-code"
                className="h-14 rounded-none border-0 border-b border-[var(--border)] focus-visible:border-black focus-visible:ring-0 px-0 text-2xl tracking-[0.35em] font-mono"
              />
              <Button
                type="submit"
                disabled={loading || code.length !== 6}
                className="mt-6 w-full rounded-full bg-black hover:bg-black/85 text-white h-14 text-base font-medium"
                data-testid="2fa-verify-submit"
              >
                {loading ? "Vérification…" : "Activer la 2FA"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
            <button
              type="button"
              onClick={() => setStep("qr")}
              className="mt-6 text-sm text-zinc-400 hover:text-[var(--text)] underline underline-offset-4"
            >
              ← Revoir le QR code
            </button>
          </>
        )}

        {step === "done" && (
          <>
            <div className="w-14 h-14 rounded-full bg-black grid place-items-center">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <h1 className="mt-6 font-display font-bold text-[var(--text)] tracking-tight leading-[1] text-4xl sm:text-5xl">
              2FA activée.
            </h1>
            <p className="mt-6 text-base text-zinc-300 max-w-lg leading-relaxed">
              À votre prochaine connexion, nous vous demanderons un code à 6 chiffres depuis votre application d'authentification.
            </p>
            <Button
              onClick={() => navigate("/security")}
              className="mt-10 rounded-full bg-black hover:bg-black/85 text-white h-14 px-8 text-base font-medium"
              data-testid="2fa-done-continue"
            >
              Retour à Sécurité
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </>
        )}
      </section>
    </div>
  );
}
